import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Check, Minus, Shield } from "lucide-react";
import { CAPABILITIES, CAPABILITY_LABELS, type Capability } from "@costadorada/shared";
import { api } from "@/lib/api";
import { roleLabel, type Role } from "@/lib/roles";
import { navSections, type NavItem } from "@/components/AppLayout";
import { QueryError } from "@/components/QueryError";

interface UserRow {
  id: string;
  firstName: string;
  lastName: string;
  role: Role;
  boardPosition?: string | null;
  permissions?: string[];
  isActive: boolean;
}

const COLUMNS: Role[] = ["ADMIN", "BOARD_MEMBER", "RESIDENT"];

type Text = { es: string; en: string };

/**
 * What a role can do on a screen beyond opening it, where that is narrower or wider than
 * "everything" — each checked against the server's own rules.
 */
const NOTES: Record<string, Partial<Record<Role, Text>>> = {
  "/meetings": { BOARD_MEMBER: { es: "Convoca la Secretaria", en: "The Secretary convenes" } },
  "/documents": { RESIDENT: { es: "Solo los documentos para titulares (Reglamento, normas, planes, comunicados)", en: "Only owner documents (bylaws, rules, plans, notices)" } },
  "/resident/works-permit": { RESIDENT: { es: "Solo las de su unidad", en: "Only their unit's" } },
  "/resident/maintenance": { RESIDENT: { es: "Las que reportó y las de su unidad", en: "Those they reported and their unit's" } },
  "/resident/announcements": {
    BOARD_MEMBER: { es: "Crear, enviar y publicar", en: "Create, send and publish" },
    RESIDENT: { es: "Solo los publicados para su unidad", en: "Only those published to their unit" },
  },
  "/finance/fees": { BOARD_MEMBER: { es: "Registrar pagos y enviar recordatorios; generar cuotas no", en: "Record payments and send reminders; not generate fees" } },
  "/finance/reminders": { BOARD_MEMBER: { es: "Solo consulta", en: "View only" } },
  "/resident/finances": { RESIDENT: { es: "Sus cuotas", en: "Their own fees" } },
};

/** Whether a role sees a sidebar entry: the same rule as the sidebar (ADMIN holds every capability). */
const sees = (item: NavItem, role: Role) => item.roles.includes(role) || (role === "ADMIN" && !!item.capability);

/**
 * Administración → Roles y permisos: what each role sees and may do, read from the same
 * sidebar definition the portal uses, plus the board seats and permissions that add to a
 * role, and who holds each one today. Roles are assigned in Usuarios → Editar.
 */
export function RolesPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isEn = lang.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const txt = (x: Text) => (isEn ? x.en : x.es);

  // Same query as Usuarios (active accounts), so it is usually already loaded.
  const { data: users = [], isError } = useQuery({
    queryKey: ["users", { showInactive: false }],
    queryFn: async () => (await api.get<UserRow[]>("/users")).data,
  });
  const active = users.filter((u) => u.isActive);
  const name = (u: UserRow) => `${u.firstName} ${u.lastName}`.trim();
  const names = (list: UserRow[]) => (list.length ? list.map(name).join(", ") : tr("Nadie", "No one"));
  const withRole = (r: Role) => active.filter((u) => u.role === r);
  const inSeat = (seat: string) => active.filter((u) => u.role === "BOARD_MEMBER" && u.boardPosition === seat);
  const withCapability = (c: Capability) => active.filter((u) => u.role !== "ADMIN" && u.permissions?.includes(c));

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-serif text-2xl text-primary-800 flex items-center gap-2">
          <Shield size={22} /> {tr("Roles y permisos", "Roles and permissions")}
        </h1>
        <p className="text-sm text-slate-600">
          {tr("Qué ve y qué puede hacer cada rol en el portal. ", "What each role sees and may do in the portal. ")}
          {tr("El rol, el cargo y los permisos de cada persona se asignan en ", "Each person's role, seat and permissions are set in ")}
          <Link to="/admin/users" className="text-primary underline">{tr("Usuarios → Editar", "Users → Edit")}</Link>.
        </p>
      </header>

      <section className="bg-white rounded-lg shadow-elegant border border-sand-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-2 text-left">{tr("Pantalla", "Screen")}</th>
              {COLUMNS.map((r) => (
                <th key={r} className="px-4 py-2 text-left">{roleLabel(r, lang)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {navSections.map((section, si) => (
              <SectionRows key={si} title={section.labelKey ? t(section.labelKey) : null}>
                {section.items.map((item) => (
                  <tr key={item.to} className="border-t border-slate-100">
                    <td className="px-4 py-2">
                      <div className="font-medium text-slate-800">{t(item.labelKey)}</div>
                      {item.capability === "finance:manage" && (
                        <div className="text-xs text-slate-500">
                          {tr("También quien tenga «Gestionar finanzas» (contabilidad) y el Tesorero", "Also whoever has “Manage finances” (bookkeeping) and the Treasurer")}
                        </div>
                      )}
                    </td>
                    {COLUMNS.map((r) => (
                      <td key={r} className="px-4 py-2 align-top">
                        {sees(item, r) ? (
                          <span className="inline-flex items-start gap-1 text-green-700">
                            <Check size={15} className="mt-0.5 shrink-0" />
                            {NOTES[item.to]?.[r] && <span className="text-xs text-slate-600">{txt(NOTES[item.to][r]!)}</span>}
                          </span>
                        ) : (
                          <Minus size={15} className="text-slate-300" aria-label={tr("No", "No")} />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </SectionRows>
            ))}
          </tbody>
        </table>
        <p className="border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          {tr(
            "Sin iniciar sesión (página pública): solo la página de inicio, con los comunicados marcados como públicos. Una cuenta con rol Público no ve ningún módulo.",
            "Without signing in (public page): only the home page, with the announcements marked public. An account with the Public role sees no module."
          )}
        </p>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="card space-y-3">
          <h2 className="font-semibold">{tr("Cargos y permisos que se suman al rol", "Seats and permissions that add to a role")}</h2>
          <ul className="space-y-2 text-sm">
            <li>
              <div className="font-medium">{tr("Secretaria de la Junta", "Board Secretary")}</div>
              <div className="text-slate-600">{tr("Convoca reuniones y envía las convocatorias.", "Convenes meetings and sends the notices.")}</div>
              <div className="text-xs text-slate-500">{names(inSeat("SECRETARY"))}</div>
            </li>
            <li>
              <div className="font-medium">{tr("Tesorero de la Junta", "Board Treasurer")}</div>
              <div className="text-slate-600">{tr("Gestiona las finanzas, como contabilidad.", "Manages finances, like bookkeeping.")}</div>
              <div className="text-xs text-slate-500">{names(inSeat("TREASURER"))}</div>
            </li>
            {CAPABILITIES.map((c) => (
              <li key={c}>
                <div className="font-medium">{txt(CAPABILITY_LABELS[c])}</div>
                <div className="text-xs text-slate-500">{names(withCapability(c))}</div>
              </li>
            ))}
          </ul>
          <p className="text-xs text-slate-500">{tr("La Administración tiene todos estos permisos.", "The administration has all of these permissions.")}</p>
        </section>

        <section className="card space-y-3">
          <h2 className="font-semibold">{tr("Solo la Administración", "Administration only")}</h2>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            <li>{tr("Eliminar registros (comunicados, cuotas, documentos, contratos, reuniones…).", "Delete records (announcements, fees, documents, contracts, meetings…).")}</li>
            <li>{tr("Usuarios, Personal, Unidades, Llaves, Cámaras y Registro de Auditoría.", "Users, Staff, Units, Keys, Cameras and the Audit Log.")}</li>
            <li>{tr("Guardar la configuración de los recordatorios de pago.", "Save the payment reminder settings.")}</li>
            <li>{tr("Generar cuotas y quitar un pago (también contabilidad).", "Generate fees and undo a payment (bookkeeping too).")}</li>
          </ul>

          <h2 className="pt-2 font-semibold">{tr("Quién tiene cada rol", "Who has each role")}</h2>
          {isError ? (
            <QueryError />
          ) : (
            <ul className="space-y-2 text-sm">
              {(["ADMIN", "BOARD_MEMBER", "RESIDENT", "PUBLIC"] as Role[]).map((r) => (
                <li key={r}>
                  <span className="font-medium">{roleLabel(r, lang)}</span>{" "}
                  <span className="text-slate-500">({withRole(r).length})</span>
                  {withRole(r).length > 0 && <div className="text-xs text-slate-500">{names(withRole(r))}</div>}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-slate-500">{tr("Solo cuentas activas.", "Active accounts only.")}</p>
        </section>
      </div>
    </div>
  );
}

function SectionRows({ title, children }: { title: string | null; children: React.ReactNode }) {
  return (
    <>
      {title && (
        <tr className="bg-slate-50/60">
          <td colSpan={COLUMNS.length + 1} className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {title}
          </td>
        </tr>
      )}
      {children}
    </>
  );
}
